import { Button, Group, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import {
  type CreateTemplateVariables,
  type StepType,
  type TemplateType,
  useCreateTemplate,
} from './api';

type CreateTemplateModalProps = {
  stepType: StepType;
  templateType: TemplateType;
  onClose: () => void;
};

export const CreateTemplateModal = ({
  stepType,
  templateType,
  onClose,
}: CreateTemplateModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { createTemplate, isCreateTemplateLoading } = useCreateTemplate();

  const canIncludeFees = templateType === 'recovery' && FEE_ELIGIBLE_STEP_TYPES.includes(stepType);

  const form = useForm({
    initialValues: {
      name: '',
      withFees: false,
    },
    validate: {
      name: value => (value.trim().length === 0 ? t(i18nKeys.ERROR.FIELD_REQUIRED) : null),
    },
  });

  const handleSubmit = form.onSubmit(({ name, withFees }) => {
    const variables: CreateTemplateVariables = {
      name,
      stepType,
      templateType,
      withFees: canIncludeFees ? withFees : undefined,
    };

    createTemplate(variables, {
      onSuccess: response => {
        onClose();
        history.push(`/settings/templates/${response.data.id}`);
      },
    });
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput
          label={t(i18nKeys.NAME)}
          withAsterisk
          data-autofocus
          {...form.getInputProps('name')}
        />
        {canIncludeFees && (
          <Switch
            label={t(i18nKeys.WITH_FEES)}
            description={t(i18nKeys.TEMPLATE_WITH_LATE_FEES)}
            {...form.getInputProps('withFees', { type: 'checkbox' })}
          />
        )}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button type="submit" loading={isCreateTemplateLoading}>
            {t(i18nKeys.CONFIRM)}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

const FEE_ELIGIBLE_STEP_TYPES: StepType[] = [
  'intermediate_reminder',
  'last_reminder',
  'additional_reminder',
];
